/**
 * kombi/admin/chrome.tsx — the Kombi replacements for admin/overlay.tsx's shared chrome.
 *
 * The eleven admin panels are not eleven independent conversions: they share one overlay frame and
 * seven small helpers, and those are what actually carry the legacy design system into them. Replace
 * these and the panels themselves need only their imports swapped.
 *
 * ── Every piece comes from an existing Kombi family ────────────────────────────────────────────
 *   AdminOverlay   -> KombiModal (the bottom drawer, extended — see KombiModal.tsx)
 *   SectionHeader  -> .catalogue-section-heading's rule-under-a-label motif, at panel scale
 *   ErrorLine      -> .kombi-field-error (itself .automation-field-error's treatment)
 *   Notice         -> .learn-note
 *   EmptyState     -> .kombi-admin-empty, .forum-no-results' italic muted line
 *   NotPermitted   -> .share-missing-state, Kombi's own "this is not available to you" block
 *   Spinner        -> .forum-submit-state's pending line. NO spinning ring: Kombi has no loader
 *                     anywhere, and a rotating border is the old design system's motif.
 *   ProvisionalNote-> .learn-note
 *
 * `useOverlayData` is NOT re-implemented. It is pure request lifecycle with no markup, so the panels
 * keep importing the shipped one — a second copy would be a second answer to "did this load".
 *
 * ── Why the modal wraps itself in `.kombi-app` ─────────────────────────────────────────────────
 * Kombi scopes every font, colour and reset to that wrapper. These panels are opened from BOTH the
 * Kombi /admin page and the legacy /admin/legacy page, and on the latter there is no wrapper — so
 * without this the panels would render with Kombi class names and none of Kombi's tokens. Wrapping
 * here makes them self-sufficient on either page. On /admin it nests a second `.kombi-app` inside the
 * page's own, which costs nothing: the declarations are identical, so applying them twice changes
 * nothing.
 */
import { useId, type ReactNode } from 'react';
import { KombiModal } from '../KombiModal';

/**
 * The admin panel frame. Same props as `AdminOverlay` so the eleven panels change only their import,
 * plus the optional eyebrow and intro KombiModal offers.
 */
export function KombiAdminOverlay({
  title,
  eyebrow,
  intro,
  onClose,
  children,
  footer,
}: {
  title: string;
  eyebrow?: string;
  intro?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="kombi-app">
      <KombiModal
        {...(eyebrow === undefined ? {} : { eyebrow })}
        {...(intro === undefined ? {} : { intro })}
        {...(footer === undefined ? {} : { footer })}
        onClose={onClose}
        title={title}
      >
        {children}
      </KombiModal>
    </div>
  );
}

/** A section heading inside a panel: a label with a hairline rule under it. */
export function SectionHeader({ children }: { children: ReactNode }) {
  return <h3 className="kombi-admin-section">{children}</h3>;
}

/** An inline error (a mutation failed), announced immediately. */
export function ErrorLine({ children, id }: { children: ReactNode; id?: string }) {
  if (!children) return null;
  return (
    <p className="kombi-field-error" {...(id === undefined ? {} : { id })} role="alert">
      {children}
    </p>
  );
}

/** A neutral notice or helper paragraph. */
export function Notice({ children }: { children: ReactNode }) {
  return <p className="learn-note">{children}</p>;
}

/** "Nobody currently holds an appointment" — an absence, stated plainly. */
export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="kombi-admin-empty">{children}</p>;
}

/**
 * The "you do not hold this appointment" state, for a panel reached without its role. Not an error:
 * nothing has gone wrong, so it says what is true and where to go, in Kombi's own missing-state block.
 */
export function NotPermitted({ what }: { what: string }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="share-missing-state">
      <h2 id={headingId}>{what}</h2>
      <p role="alert">
        You don&rsquo;t currently hold the appointment this screen needs. If you believe you should,
        ask the platform owner to check the administration surface.
      </p>
    </section>
  );
}

/**
 * The fetch-on-open pending state.
 *
 * NO SPINNING RING, deliberately. The teal one animated a rotating border; Kombi has no loader motif
 * anywhere in 9,000 lines of CSS, and inventing a spinner would be inventing a visual language. It
 * says so in words instead, which is also what a screen reader gets either way.
 */
export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <p className="kombi-admin-pending" role="status">
      {label}
    </p>
  );
}

/** The standing provisional-status note every panel carries. */
export function ProvisionalNote() {
  return (
    <p className="learn-note" role="note">
      <strong>Provisional interface.</strong> A working engineering build for an appointed
      administrator. Its layout and wording are not the final design.
    </p>
  );
}

/**
 * A panel's primary action, in the drawer archetype's own idiom: transparent, ink text, the acid dot,
 * an arrow that nudges. NOT a filled rectangle — that is the old design system's primary button, and
 * on this panel it reads as one.
 */
export function PrimaryAction({
  children,
  onClick,
  disabled,
  title,
  arrow = true,
  submit = false,
}: {
  children: ReactNode;
  /** Absent on a submit button, whose form owns the action. */
  onClick?: () => void;
  disabled?: boolean;
  /** Terminal actions (Close, Done) take no arrow; forward ones do. */
  arrow?: boolean;
  /** A real form submit, so Enter in a field still submits and the browser validates first. */
  submit?: boolean;
  /** A tooltip. Several panels use it to explain WHY an action is unavailable, so it must survive. */
  title?: string;
}) {
  return (
    <button
      className="catalogue-text-link kombi-modal-primary"
      disabled={disabled ?? false}
      {...(onClick === undefined ? {} : { onClick })}
      {...(title === undefined ? {} : { title })}
      type={submit ? 'submit' : 'button'}
    >
      {children}
      {arrow && <span aria-hidden="true">→</span>}
    </button>
  );
}

/** A panel's quiet action — Cancel, Back, or a destructive one kept away from the primary. */
export function QuietAction({
  children,
  onClick,
  disabled,
  title,
  left,
  danger,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  /** Pushes it to the left of the footer row. */
  left?: boolean;
  /**
   * A revoke or a remove. It stays a quiet underlined control rather than becoming a filled danger
   * button — the colour is the warning, and a filled red rectangle is the old design system's idiom.
   */
  danger?: boolean;
  /** A tooltip. Several panels use it to explain WHY an action is unavailable, so it must survive. */
  title?: string;
}) {
  return (
    <button
      className={`kombi-modal-quiet${left === true ? ' kombi-modal-footer-left' : ''}${danger === true ? ' kombi-admin-danger' : ''}`}
      disabled={disabled ?? false}
      {...(onClick === undefined ? {} : { onClick })}
      {...(title === undefined ? {} : { title })}
      type="button"
    >
      {children}
    </button>
  );
}
